import { computed, defineComponent } from "vue";
import { Container } from "aurelia-dependency-injection";
import { mapState } from "pinia";
import moment from "moment";

import { CalendarOptions, EventContentArg } from "@fullcalendar/core";
import FullCalendar from "@fullcalendar/vue3";
import timeGridPlugin from "@fullcalendar/timegrid";
import dayGridPlugin from "@fullcalendar/daygrid";
import interactionPlugin from "@fullcalendar/interaction";

import eventBus from "../../../utilities/eventBus";
import { formatDate } from "../../../common/vue-helpers/modifiers/value-modifier";
import { useApp } from "../../App/useApp";
import { MeetingCalendarService } from "../../../services/meeting-calendar-service";
import { ToastrService } from "../../../services/toastr.service";
import type { IMeetingDetails } from "../../../resources-vue/vue-interfaces/messaging-service/i-meeting-calendar";
import { CalenderViewEnum } from "../../../enums/MeetingCalendarEnum";

import AddCalendarEvent from "../../../resources-vue/vue-dialogs/AddCalendarEvent/AddCalendarEvent.vue";
import MeetingDetails from "../../../resources-vue/vue-dialogs/MeetingDetails/MeetingDetails.vue";

interface ICalendarEvent {
    id: string;
    title: string;
    start: string;
    end: string;
    allDay: boolean;
    extendedProps: {
        description: string;
        isOrganizer: boolean;
        organizerDetails: any;
        roomId: string;
        conversationId: string;
        recurringType: number;
        isRecurring: boolean;
        recurrenceStartDate: string;
        recurrenceEndDate: string;
        participants: any;
        frequency: number;
        weeklyDaysOfWeekStr: string;
    };
}

export default defineComponent({
    components: {
        FullCalendar
    },

    data() {
        return {
            dialogRef: null,
            _meetingCalendarService: null as MeetingCalendarService,
            _toastrService: null as ToastrService,
            events: [] as ICalendarEvent[],
            latestRequestKey: "" as string,
            selectedView: "timeGridWeek", // default view
            dateRangeLabel: "" as string,
            startDate: moment().format("YYYY-MM-DD") as string,
            endDate: null as string,
            weekStartDate: moment().format("YYYY-MM-DD") as string,
            formatDate
        };
    },

    provide() {
        return {
            dialogRef: computed(() => this.dialogRef)
        };
    },

    computed: {
        ...mapState(useApp, ["loggedInUserDetails"]),

        calendarOptions(): CalendarOptions {
            return {
                plugins: [timeGridPlugin, dayGridPlugin, interactionPlugin],
                initialView: "timeGridWeek",
                initialDate: this.startDate,
                eventMaxStack: 2,
                dayPopoverFormat: {
                    weekday: "long",
                    day: "numeric",
                    month: "short",
                    year: "numeric"
                },

                moreLinkClassNames: ["meeting-more-link"],
                moreLinkContent: (arg) => {
                    return {
                        html: `
      <div class="more-count">+${arg.num}</div>
      <div class="more-text">More</div>
    `
                    };
                },
                firstDay: 1, // starts week from Monday, 1=Mon
                headerToolbar: false, // hide built-in toolbar
                views: {
                    timeGridWeek: {
                        dayHeaderFormat: {
                            day: "2-digit", // 02
                            weekday: "long" // Monday
                        }
                    },
                    timeGridDay: {
                        dayHeaderFormat: {
                            day: "2-digit", // 02
                            weekday: "long" // Monday
                        }
                    },
                    dayGridMonth: {
                        dayHeaderFormat: {
                            weekday: "short" // Mon Tue Wed…
                        }
                    }
                },
                events: this.events,

                eventDidMount: () => {
                    setTimeout(() => {
                        this.fixMultipleEventLayout();
                    }, 0);
                },

                moreLinkDidMount: () => {
                    setTimeout(() => {
                        this.fixMultipleEventLayout();
                    }, 0);
                },

                datesSet: this.handleDatesSet,

                slotEventOverlap: false,
                allDaySlot: this.handleAllDaySlot,
                eventContent: (arg: EventContentArg) => {
                    const { title, start, end, allDay, extendedProps } = arg.event;
                    const isRecurring = extendedProps?.isRecurring;
                    const startTime = moment.utc(start).local();
                    const endTime = moment.utc(end).local();
                    const durationInMinutes = endTime.diff(startTime, "minutes");
                    //const organizerName = "XYZ"; add organizer name in UI ticket
                    let htmlContent;
                    if (durationInMinutes <= 15 || allDay) {
                        htmlContent = `
    <div id="Meeting__Event"
         class="${durationInMinutes <= 15 ? "hide-main-time" : ""}">
      <div class="title fw-bold text-truncate">
        ${title}
        ${isRecurring ? `<img class="recurrence-icon" src="${require("../../../assets/images/chat/recurrence-icon.svg")}" />` : ""}
      </div>
      ${!allDay ? `<div class="meeting-time">${startTime.format("hh:mm A")} - ${endTime.format("hh:mm A")}</div>` : ""}
    </div>
  `;
                    } else if (durationInMinutes > 15 && durationInMinutes <= 30) {
                        htmlContent = `
                        <div id="Meeting__Event">
                            <div class="title fw-bold text-truncate">${title}</div>
                            <div class="meeting-time">${startTime.format("hh:mm A")} - ${endTime.format("hh:mm A")}</div>
                            ${isRecurring ? `<img class="recurrence-icon" src="${require("../../../assets/images/chat/recurrence-icon.svg")}" />` : ""}
                        </div>
                        `;
                    } else {
                        htmlContent = `
                        <div id="Meeting__Event">
                            <div class="title fw-bold text-truncate">${title}</div>
                            <div class="meeting-time">${startTime.format("hh:mm A")} - ${endTime.format("hh:mm A")}</div>
                            ${isRecurring ? `<img class="recurrence-icon" src="${require("../../../assets/images/chat/recurrence-icon.svg")}" />` : ""}
                        </div>
                        `;
                    }
                    return {
                        html: htmlContent
                    };
                },
                eventClick: this.handleEventClick
            };
        },
        handleAllDaySlot(): boolean {
            let showAllDaySlot = false;
            this.events.forEach((event: ICalendarEvent) => {
                if (event.allDay) {
                    showAllDaySlot = true;
                }
            });
            return showAllDaySlot;
        }
    },

    created() {
        this._meetingCalendarService = Container.instance.get(MeetingCalendarService);
        this._toastrService = Container.instance.get(ToastrService);
    },

    mounted() {
        eventBus.on("updateMeetingCalendar", () => {
            this.getEvents(CalenderViewEnum.week, this.weekStartDate);
        });
        this.lockMorePopoverScroll();
    },

    methods: {
        getApi() {
            return (this.$refs.meetingCalendar as any).getApi();
        },
        goToday() {
            this.getApi().today();
        },
        goPrev() {
            this.getApi().prev();
        },
        goNext() {
            this.getApi().next();
        },
        changeView() {
            this.getApi().changeView(this.selectedView);
        },

        lockMorePopoverScroll() {
            const stopScroll = (event: WheelEvent) => {
                const popover = document.querySelector("#Meeting__Calendar .fc-popover.fc-more-popover");

                if (!popover) return;

                const target = event.target as Node;

                // Allow scrolling inside the popover
                if (popover.contains(target)) {
                    return;
                }

                // Prevent background page scrolling
                event.preventDefault();
            };

            const observer = new MutationObserver(() => {
                const popover = document.querySelector("#Meeting__Calendar .fc-popover.fc-more-popover");

                if (popover) {
                    document.addEventListener("wheel", stopScroll, {
                        passive: false,
                        capture: true
                    });
                } else {
                    document.removeEventListener("wheel", stopScroll, true);
                }
            });

            observer.observe(document.body, {
                childList: true,
                subtree: true
            });
        },

        fixMultipleEventLayout() {
            const columns = document.querySelectorAll(".fc-timegrid-col-events");

            columns.forEach((column) => {
                const eventHarnesses = Array.from(
                    column.querySelectorAll(".fc-timegrid-event-harness")
                ) as HTMLElement[];

                // Group events having the same top position
                const groups: HTMLElement[][] = [];

                eventHarnesses.forEach((event) => {
                    const rect = event.getBoundingClientRect();

                    const group = groups.find((events) => {
                        const firstRect = events[0].getBoundingClientRect();
                        return Math.abs(firstRect.top - rect.top) < 3;
                    });

                    if (group) {
                        group.push(event);
                    } else {
                        groups.push([event]);
                    }
                });

                // Handle exactly 2 events
                groups.forEach((group) => {
                    if (group.length === 2) {
                        group.forEach((event) => {
                            const timeElement = event.querySelector(".meeting-time") as HTMLElement | null;

                            if (timeElement) {
                                const time = timeElement.textContent?.trim() || "";

                                // Keep only start time
                                if (time.includes(" - ")) {
                                    timeElement.textContent = time.split(" - ")[0];
                                }
                            }
                        });
                    }
                });

                // Handle 3+ events (+More)
                const moreLinks = column.querySelectorAll(".fc-timegrid-more-link");

                moreLinks.forEach((moreLink) => {
                    const moreElement = moreLink as HTMLElement;
                    const moreRect = moreElement.getBoundingClientRect();

                    const matchingEvents = eventHarnesses.filter((event) => {
                        const eventRect = event.getBoundingClientRect();

                        const verticalOverlap =
                            Math.min(eventRect.bottom, moreRect.bottom) - Math.max(eventRect.top, moreRect.top);

                        return verticalOverlap > 0;
                    });

                    if (matchingEvents.length === 2) {
                        const width = 33.3333;

                        matchingEvents[0].style.left = "0%";
                        matchingEvents[0].style.width = `${width}%`;
                        matchingEvents[0].style.right = "auto";

                        matchingEvents[1].style.left = `${width}%`;
                        matchingEvents[1].style.width = `${width}%`;
                        matchingEvents[1].style.right = "auto";

                        moreElement.style.left = `${width * 2}%`;
                        moreElement.style.width = `${width}%`;
                        moreElement.style.right = "auto";
                        moreElement.style.boxSizing = "border-box";
                    }
                });
            });
        },

        mapViewTypeToCalView(viewType: string): number {
            switch (viewType) {
                case "timeGridDay":
                    return 1;
                case "timeGridWeek":
                    return 2;
                case "dayGridMonth":
                    return 3;
                default:
                    return 2;
            }
        },

        formatandMapMeetingEvents(allEvents: IMeetingDetails): ICalendarEvent[] {
            if (!allEvents) {
                return [];
            }
            return Object.entries(allEvents).flatMap(([date, events]) => {
                return events.map((event) => {
                    let organizerDetails = null;
                    let participants = [];
                    let isOrganizer = false;

                    event.participants.forEach((participant) => {
                        if (participant.loginId === this.loggedInUserDetails.loginId) {
                            isOrganizer = participant.isOrganizer;
                        }

                        if (participant.isOrganizer) {
                            // store organizer details
                            organizerDetails = `${participant.firstName} ${participant.lastName}`;
                        } else {
                            // store other participant names
                            participants.push(participant);
                        }
                    });

                    return {
                        id: event.id,
                        title: event.title || "Meeting",
                        start: moment.utc(event.startDateTime).local().format("YYYY-MM-DD HH:mm:ss"),
                        end: moment.utc(event.endDateTime).local().format("YYYY-MM-DD HH:mm:ss"),
                        allDay: event.isAllDay,
                        extendedProps: {
                            description: event.desc,
                            isOrganizer,
                            organizerDetails,
                            roomId: event.roomId,
                            conversationId: event.conversationId,
                            recurringType: event.recurringType,
                            isRecurring: event.isRecurring,
                            recurrenceStartDate: event.recurrenceStartDateTime,
                            recurrenceEndDate: event.recurrenceEndDateTime,
                            participants,
                            frequency: event.frequency,
                            weeklyDaysOfWeekStr: event.weeklyDaysOfWeekStr
                        }
                    };
                });
            });
        },

        async getEvents(calView: CalenderViewEnum, startDate: string) {
            if (!this.loggedInUserDetails.loginId || !this._meetingCalendarService) {
                this.events = [];
                return;
            }

            try {
                const response = await this._meetingCalendarService.getScheduledMeetings({
                    userId: this.loggedInUserDetails.loginId,
                    calView,
                    startDateTime: startDate
                });

                this.events = this.formatandMapMeetingEvents(response?.meetingDetails);
            } catch (error) {
                console.error("Failed to fetch scheduled meetings", error);
                this.events = [];
            }
        },

        async handleDatesSet(dateInfo: any) {
            const start = dateInfo.start;
            const end = dateInfo.end;
            const viewType = dateInfo.view.type;
            const calView = this.mapViewTypeToCalView(viewType);

            this.weekStartDate = moment(start).format("YYYY-MM-DD[T]HH:mm:ss");

            if (calView === CalenderViewEnum.week) {
                const options: Intl.DateTimeFormatOptions = { month: "long", day: "numeric", year: "numeric" };
                const startStr = start.toLocaleDateString("en-US", options);

                // subtract 1 day from end because FullCalendar's end is exclusive
                const endDate = new Date(end);
                endDate.setDate(endDate.getDate() - 1);
                const endStr = endDate.toLocaleDateString("en-US", options);
                this.dateRangeLabel = `${startStr} - ${endStr}`;
            } else if (calView === CalenderViewEnum.month) {
                this.dateRangeLabel = dateInfo.view.currentStart.toLocaleDateString("en-US", {
                    month: "long",
                    year: "numeric"
                });
                this.weekStartDate = moment(dateInfo.view.currentStart).format("YYYY-MM-DD[T]HH:mm:ss");
            } else if (calView === CalenderViewEnum.day) {
                this.dateRangeLabel = start.toLocaleDateString("en-US", {
                    day: "numeric",
                    month: "long",
                    year: "numeric"
                });
            }

            const requestKey = `${calView}-${this.weekStartDate}`;

            if (requestKey === this.latestRequestKey) {
                return;
            }

            this.latestRequestKey = requestKey;
            await this.getEvents(calView, this.weekStartDate);
        },

        handleNewEventClick() {
            this.dialogRef = this.$dialog.open(AddCalendarEvent, {
                props: {
                    modal: true,
                    showHeader: false
                },
                data: {
                    conversationId: this.$route?.params?.id || ""
                },
                onClose: async (response: any) => {
                    if (response.data === "success") {
                        this.latestRequestKey = "";
                        const startDate = new Date().toISOString();
                        await this.getEvents(CalenderViewEnum.week, startDate);
                        this._toastrService.success({
                            title: "Created",
                            message: "Meeting created successfully"
                        });
                    }
                }
            });
        },

        handleEventClick(event: any) {
            event.jsEvent.preventDefault();
            event.jsEvent.stopPropagation();

            this.openDetailsDialog(event.event);
        },

        openDetailsDialog(eventData: any) {
            this.dialogRef = this.$dialog.open(MeetingDetails, {
                props: {
                    modal: true,
                    showHeader: false
                },
                data: {
                    selectedEvent: eventData
                }
            });
        }
    },

    beforeUnmount() {
        eventBus.off("updateMeetingCalendar");
    }
});
